import { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';
import moment from 'moment';
import _ from 'lodash';

import {
  ChainedOfferEventStatus,
  DeleteSongpopLegacyPackDropMutation,
  DeleteSongpopLegacyPackDropMutationVariables,
  GetSpendTierAudienceQuery,
  GetSpendTierAudienceQueryVariables,
  SongpopLegacyPackNotifAnchor,
  SongpopLegacyPackNotifScheduleEntryInput,
  UpdateSongpopLegacyPackDropMutation,
  UpdateSongpopLegacyPackDropMutationVariables,
  UserSpendTier,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { SPEND_TIER_AUDIENCE_QUERY } from '../../SaleEvent/graphql';
import { applyChangeIfDateValid } from '../../../../../../utils/datepicker';
import { DELETE_SONGPOP_LEGACY_PACK_DROP, UPDATE_SONGPOP_LEGACY_PACK_DROP } from '../graphql';
import {
  APP_VERSION_OPERATORS,
  dropStatusBadge,
  LegacyPackDrop,
  LegacyPackSkuOption,
  skuOptionLabel,
  textToUserIds,
  toRewardInput,
  userIdsToText,
} from '../utils';

const ALL_TIERS = 'All';

type Props = {
  drop: LegacyPackDrop;
  skuOptions: LegacyPackSkuOption[];
  onSaved: (drop: LegacyPackDrop) => void;
  onDeleted: (dropId: string) => void;
  // Every other sale config, to warn about two ACTIVE ones selling the same price point at once.
  otherDrops: LegacyPackDrop[];
};

// Same tiers, labels and ranges as the sale event matchers (SaleEvent/components/SaleEventData.tsx).
const SPEND_TIER_OPTIONS = [
  { tier: UserSpendTier.KRAKEN, label: 'Kraken', range: 'USD > 1,000' },
  { tier: UserSpendTier.WHALE, label: 'Whale', range: 'USD 100 - 1,000' },
  { tier: UserSpendTier.DOLPHIN, label: 'Dolphin', range: 'USD 20 - 100' },
  { tier: UserSpendTier.MINNOW, label: 'Minnow', range: 'USD 0 - 20' },
  { tier: UserSpendTier.NPAU, label: 'NPAU', range: 'never paid' },
] as const;

// Same limits as the backend (admin/drops.ts).
const MAX_NOTIF_TITLE_LENGTH = 50;
const MAX_NOTIF_BODY_LENGTH = 180;

// Display only: the store charges the price point. Same options as the sale bundles.
const DISCOUNT_OPTIONS = [
  { name: 'None', value: 0 },
  ...[10, 20, 30, 40, 50, 60, 70, 80, 90].map((value) => ({ name: `${value}%`, value })),
];

function computeOldPrice(newPrice: number | undefined, discount: number): number | null {
  return discount && newPrice ? Number(((newPrice * 100) / (100 - discount)).toFixed(2)) : null;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Card className="mb-4 shadow-sm">
      <Card.Header className="bg-light text-center">
        <h5 className="mb-0">{title}</h5>
        {hint && <small className="text-muted">{hint}</small>}
      </Card.Header>
      <Card.Body>{children}</Card.Body>
    </Card>
  );
}

const ALL_SPEND_TIERS_COUNT = SPEND_TIER_OPTIONS.length;
const optionalNumber = (value: string) => (value.trim() === '' ? null : Number(value));

// Raw form values of a stored drop, in the shape the form state holds them.
function formValuesFromDrop(drop: LegacyPackDrop) {
  return {
    name: drop.name,
    status: drop.status,
    startDate: new Date(drop.startDate),
    stopDate: new Date(drop.stopDate),
    // true = the cap applies, null/false = no cap (the default)
    capEnabled: !!drop.capEnabled,
    capSeats: String(drop.capSeats),
    skuId: drop.skuId,
    offerPercentage: drop.offerPercentage?.toString() ?? '',
    rewards: drop.instantRewards.map(toRewardInput),
    schedule: drop.notifSchedule.map(
      ({ anchor, offsetHours, title, body }): SongpopLegacyPackNotifScheduleEntryInput => ({
        anchor,
        offsetHours,
        title,
        body,
      })
    ),
    filterUserIds: userIdsToText(drop.filterUserIds),
    filterSubscriptionTier: (drop.filterSubscriptionTier as string) ?? ALL_TIERS,
    filterMinCreation: drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined,
    filterMaxCreation: drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined,
    filterAppVersion: drop.filterAppVersion ?? '',
    filterAppVersionOperator: drop.filterAppVersionOperator ?? '>=',
    filterUserLevel: drop.filterUserLevel?.toString() ?? '',
    filterExcludedUserIds: userIdsToText(drop.filterExcludedUserIds),
    filterHadSub: !!drop.filterHadSub,
    filterSpendTiers: (drop.filterSpendTiers ?? []) as UserSpendTier[],
  };
}
type FormValues = ReturnType<typeof formValuesFromDrop>;

// The mutation input for some form values. Also used to tell whether anything changed: the form
// is dirty exactly when this differs from the input of the stored drop.
function buildDropInput(id: string, values: FormValues, skuOptions: LegacyPackSkuOption[]) {
  const discount = Number(values.offerPercentage) || 0;
  const cost = skuOptions.find((option) => option.id === values.skuId)?.cost;
  const appVersion = values.filterAppVersion.trim();
  const spendTiers = values.filterSpendTiers;
  return {
    id,
    name: values.name,
    status: values.status,
    startDate: values.startDate,
    stopDate: values.stopDate,
    // off is stored as null (same meaning), so toggling on then off again is not a change
    capEnabled: values.capEnabled ? true : null,
    // Kept while the cap is off, so switching it back on finds the same number.
    capSeats: Number(values.capSeats),
    skuId: values.skuId,
    // Same as the sale bundles: only the discount is picked, the crossed-out price follows from it.
    priceUsdBase: computeOldPrice(cost, discount),
    offerPercentage: discount || null,
    instantRewards: values.rewards.map(({ type, amount, itemId }) => ({ type, amount, itemId: itemId ?? undefined })),
    notifSchedule: values.schedule.map(({ anchor, offsetHours, title, body }) => ({
      anchor,
      offsetHours: Number(offsetHours),
      title: title?.trim() || null,
      body: body?.trim() || null,
    })),
    filterUserIds: textToUserIds(values.filterUserIds),
    filterSubscriptionTier:
      values.filterSubscriptionTier === ALL_TIERS ? undefined : (values.filterSubscriptionTier as UserSubscriptionTier),
    filterUserMinCreationDate: values.filterMinCreation,
    filterUserMaxCreationDate: values.filterMaxCreation,
    filterAppVersion: appVersion || undefined,
    filterAppVersionOperator: appVersion ? values.filterAppVersionOperator : undefined,
    filterUserLevel: optionalNumber(values.filterUserLevel) ?? undefined,
    filterExcludedUserIds: textToUserIds(values.filterExcludedUserIds),
    filterHadSub: values.filterHadSub || undefined,
    filterSpendTiers: spendTiers.length > 0 && spendTiers.length < ALL_SPEND_TIERS_COUNT ? spendTiers : undefined,
  };
}

// Dates compared by instant, key order and tier order ignored.
function sameInput(a: ReturnType<typeof buildDropInput>, b: ReturnType<typeof buildDropInput>): boolean {
  const normalize = (input: ReturnType<typeof buildDropInput>) =>
    JSON.parse(
      JSON.stringify({ ...input, filterSpendTiers: input.filterSpendTiers && [...input.filterSpendTiers].sort() })
    );
  return _.isEqual(normalize(a), normalize(b));
}

/**
 * One drop = one sale window. It only controls who SEES the offer: the purchase itself never
 * checks cap, dates or status, so seatsSold can go above capSeats (accepted oversell): a valid
 * payment is always granted, even after the cap or the stop date.
 * Beta and prod share this data: saving here is live for both builds.
 */
export function DropData({ drop, skuOptions, onSaved, onDeleted, otherDrops }: Props) {
  const [name, setName] = useState(drop.name);
  const [status, setStatus] = useState(drop.status);
  const [startDate, setStartDate] = useState<Date>(new Date(drop.startDate));
  const [stopDate, setStopDate] = useState<Date>(new Date(drop.stopDate));
  const [capEnabled, setCapEnabled] = useState(!!drop.capEnabled);
  const [capSeats, setCapSeats] = useState(String(drop.capSeats));
  const [skuId, setSkuId] = useState(drop.skuId);
  const [offerPercentage, setOfferPercentage] = useState(drop.offerPercentage?.toString() ?? '');
  const [schedule, setSchedule] = useState<SongpopLegacyPackNotifScheduleEntryInput[]>(
    drop.notifSchedule.map(({ anchor, offsetHours, title, body }) => ({ anchor, offsetHours, title, body }))
  );

  const [filterUserIds, setFilterUserIds] = useState(userIdsToText(drop.filterUserIds));
  const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<string>(
    drop.filterSubscriptionTier ?? ALL_TIERS
  );
  const [filterMinCreation, setFilterMinCreation] = useState<Date | undefined>(
    drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined
  );
  const [filterMaxCreation, setFilterMaxCreation] = useState<Date | undefined>(
    drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined
  );
  const [filterAppVersion, setFilterAppVersion] = useState(drop.filterAppVersion ?? '');
  const [filterAppVersionOperator, setFilterAppVersionOperator] = useState(drop.filterAppVersionOperator ?? '>=');
  const [filterUserLevel, setFilterUserLevel] = useState(drop.filterUserLevel?.toString() ?? '');
  const [filterExcludedUserIds, setFilterExcludedUserIds] = useState(userIdsToText(drop.filterExcludedUserIds));
  const [filterHadSub, setFilterHadSub] = useState(!!drop.filterHadSub);
  const [filterSpendTiers, setFilterSpendTiers] = useState<UserSpendTier[]>(drop.filterSpendTiers ?? []);

  // Explicit action, as in the sale events: the count scans every payer row.
  const [loadSpendTierAudience, { data: spendTierAudienceData, loading: spendTierAudienceLoading }] = useLazyQuery<
    GetSpendTierAudienceQuery,
    GetSpendTierAudienceQueryVariables
  >(SPEND_TIER_AUDIENCE_QUERY, { fetchPolicy: 'no-cache' });
  const spendTierAudienceByTier = useMemo(() => {
    const rows = spendTierAudienceData?.liveOps.spendTierAudience ?? [];
    return rows.reduce<Record<string, typeof rows[number]>>((acc, row) => ({ ...acc, [row.tier]: row }), {});
  }, [spendTierAudienceData]);
  const spendTierComputedAt = spendTierAudienceData?.liveOps.spendTierAudience?.find(
    (row) => row.computedAt
  )?.computedAt;
  // Not evaluable over the whole population (request header / composite tables): still enforced at runtime.
  const audienceIgnoredMatchers = [
    filterAppVersion.trim() ? 'app version' : undefined,
    filterSubscriptionTier !== ALL_TIERS ? 'subscription tier' : undefined,
  ].filter((matcher): matcher is string => !!matcher);
  // Every tier ticked is saved as "All users": against an explicit list, a payer whose tier has not
  // synced yet would fail closed.
  const allSpendTiersSelected = filterSpendTiers.length === SPEND_TIER_OPTIONS.length;
  const toggleSpendTier = (tier: UserSpendTier) =>
    setFilterSpendTiers((old) => (old.includes(tier) ? old.filter((t) => t !== tier) : [...old, tier]));

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    // New drop selected: reset the form to its stored values.
    setName(drop.name);
    setStatus(drop.status);
    setStartDate(new Date(drop.startDate));
    setStopDate(new Date(drop.stopDate));
    setCapEnabled(!!drop.capEnabled);
    setCapSeats(String(drop.capSeats));
    setSkuId(drop.skuId);
    setOfferPercentage(drop.offerPercentage?.toString() ?? '');
    setSchedule(
      drop.notifSchedule.map(({ anchor, offsetHours, title, body }) => ({ anchor, offsetHours, title, body }))
    );
    setFilterUserIds(userIdsToText(drop.filterUserIds));
    setFilterSubscriptionTier(drop.filterSubscriptionTier ?? ALL_TIERS);
    setFilterMinCreation(drop.filterUserMinCreationDate ? new Date(drop.filterUserMinCreationDate) : undefined);
    setFilterMaxCreation(drop.filterUserMaxCreationDate ? new Date(drop.filterUserMaxCreationDate) : undefined);
    setFilterAppVersion(drop.filterAppVersion ?? '');
    setFilterAppVersionOperator(drop.filterAppVersionOperator ?? '>=');
    setFilterUserLevel(drop.filterUserLevel?.toString() ?? '');
    setFilterExcludedUserIds(userIdsToText(drop.filterExcludedUserIds));
    setFilterHadSub(!!drop.filterHadSub);
    setFilterSpendTiers(drop.filterSpendTiers ?? []);
  }, [drop]);

  const [updateDrop, { loading: saving, error: saveError }] = useMutation<
    UpdateSongpopLegacyPackDropMutation,
    UpdateSongpopLegacyPackDropMutationVariables
  >(UPDATE_SONGPOP_LEGACY_PACK_DROP, {
    onCompleted: ({ updateSongpopLegacyPackDrop }) => onSaved(updateSongpopLegacyPackDrop),
  });
  const [deleteDrop, { loading: deleting, error: deleteError }] = useMutation<
    DeleteSongpopLegacyPackDropMutation,
    DeleteSongpopLegacyPackDropMutationVariables
  >(DELETE_SONGPOP_LEGACY_PACK_DROP, {
    onCompleted: ({ deleteSongpopLegacyPackDrop }) => onDeleted(deleteSongpopLegacyPackDrop),
  });

  const statusBadge = dropStatusBadge(drop);
  // One live event at a time (the backend refuses the save): shown before saving.
  const overlapping =
    status === ChainedOfferEventStatus.ACTIVE
      ? otherDrops.filter(
          (other) =>
            other.id !== drop.id &&
            other.status === ChainedOfferEventStatus.ACTIVE &&
            new Date(other.startDate) < stopDate &&
            startDate < new Date(other.stopDate)
        )
      : [];
  const selectedSku = skuOptions.find((option) => option.id === skuId);
  const discount = Number(offerPercentage) || 0;
  const oldPrice = computeOldPrice(selectedSku?.cost, discount);

  const currentInput = buildDropInput(
    drop.id,
    {
      name,
      status,
      startDate,
      stopDate,
      capEnabled,
      capSeats,
      skuId,
      offerPercentage,
      // unused (the instant rewards are global, see LegacyPackSettings): sent back as stored
      rewards: drop.instantRewards.map(toRewardInput),
      schedule,
      filterUserIds,
      filterSubscriptionTier,
      filterMinCreation,
      filterMaxCreation,
      filterAppVersion,
      filterAppVersionOperator,
      filterUserLevel,
      filterExcludedUserIds,
      filterHadSub,
      filterSpendTiers,
    },
    skuOptions
  );
  const isDirty = !sameInput(currentInput, buildDropInput(drop.id, formValuesFromDrop(drop), skuOptions));

  const handleSave = () => {
    updateDrop({ variables: { input: currentInput } }).catch(() => {
      // surfaced through saveError
    });
  };

  const updateScheduleEntry = (index: number, entry: Partial<SongpopLegacyPackNotifScheduleEntryInput>) =>
    setSchedule((previous) => previous.map((current, i) => (i === index ? { ...current, ...entry } : current)));

  const previewFireDate = (entry: SongpopLegacyPackNotifScheduleEntryInput) =>
    moment(entry.anchor === SongpopLegacyPackNotifAnchor.START ? startDate : stopDate)
      .add(Number(entry.offsetHours) || 0, 'hours')
      .format('YYYY-MM-DD HH:mm');

  return (
    <>
      <Container className="pb-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0">
              {drop.name}{' '}
              <Badge pill variant={statusBadge.variant} className="align-middle">
                {statusBadge.label}
              </Badge>
            </h4>
          </Col>
          <Col xs="auto" className="d-flex justify-content-end">
            <Button
              className="mr-2"
              variant="danger"
              disabled={deleting || drop.seatsSold > 0}
              title={drop.seatsSold > 0 ? 'Something was sold through this sale config: set it to DRAFT instead' : ''}
              onClick={() => setShowDeleteModal(true)}
            >
              <i className="fas fa-trash mr-2" />
              Delete
            </Button>
            <Button
              variant="success"
              disabled={saving || !isDirty}
              title={isDirty ? '' : 'Nothing changed'}
              onClick={handleSave}
            >
              {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
            </Button>
          </Col>
        </Row>

        {saveError && <Alert variant="danger">{saveError.message}</Alert>}
        {deleteError && <Alert variant="danger">{deleteError.message}</Alert>}
        {overlapping.length > 0 && (
          <Alert variant="warning">
            <strong>Another event is live on these dates:</strong> {overlapping.map((other) => other.name).join(', ')}.
            Only one event can be live at a time: set that one to DRAFT or move the dates, otherwise this save is
            refused.
          </Alert>
        )}

        <Section title="General">
          <Row className="mb-3">
            <Col md={3}>Id:</Col>
            <Col md={5}>
              <code>{drop.id}</code>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Name:</Col>
            <Col md={5}>
              <Form.Control value={name} onChange={(e) => setName(e.target.value)} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Status:</Col>
            <Col md={3}>
              <Form.Control
                as="select"
                value={status}
                onChange={(e) => setStatus(e.target.value as ChainedOfferEventStatus)}
              >
                {Object.values(ChainedOfferEventStatus).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Start date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStartDate)}
                // @ts-ignore
                value={startDate}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Stop date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setStopDate)}
                // @ts-ignore
                value={stopDate}
              />
            </Col>
          </Row>
        </Section>

        <Section title="Matchers" hint="Who sees this offer. Empty = everyone.">
          <Row className="mb-3">
            <Col md={3}>Minimum registration date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterMinCreation)}
                // @ts-ignore
                value={filterMinCreation}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Maximum registration date:</Col>
            <Col md={5}>
              <DateTimePicker
                dateFormat="YYYY-MM-DD"
                timeFormat
                // @ts-ignore
                onChange={applyChangeIfDateValid(setFilterMaxCreation)}
                // @ts-ignore
                value={filterMaxCreation}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>App version:</Col>
            <Col md={2}>
              <Form.Control
                as="select"
                value={filterAppVersionOperator}
                onChange={(e) => setFilterAppVersionOperator(e.target.value)}
              >
                {APP_VERSION_OPERATORS.map((operator) => (
                  <option key={operator} value={operator}>
                    {operator}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder="any"
                value={filterAppVersion}
                onChange={(e) => setFilterAppVersion(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Min user level:</Col>
            <Col md={3}>
              <Form.Control
                placeholder="any"
                value={filterUserLevel}
                onChange={(e) => setFilterUserLevel(e.target.value)}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Subscription tier:</Col>
            <Col md={3}>
              <Form.Control
                as="select"
                value={filterSubscriptionTier}
                onChange={(e) => setFilterSubscriptionTier(e.target.value)}
              >
                <option value={ALL_TIERS}>All</option>
                {Object.values(UserSubscriptionTier).map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Form.Control>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              User ids:
              <br />
              (separated by commas)
            </Col>
            <Col md={8}>
              <Form.Control as="textarea" value={filterUserIds} onChange={(e) => setFilterUserIds(e.target.value)} />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Never subscribed:</Col>
            <Col md={5}>
              <Form.Check
                type="checkbox"
                id={`legacy-drop-had-sub-${drop.id}`}
                checked={filterHadSub}
                onChange={(e) => setFilterHadSub(e.target.checked)}
                label={<small className="text-muted">only users who never bought a subscription</small>}
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Not an owner yet:</Col>
            <Col md={8}>
              {/* Display only: the backend never offers the pack to an owner (getVisibleLegacyPackOffer) */}
              <Form.Check
                type="checkbox"
                id={`legacy-drop-not-owner-${drop.id}`}
                checked
                disabled
                label={
                  <small className="text-muted">
                    only users who don&apos;t own Songpop Maestro yet (always on, owners never see the offer or the
                    pushes)
                  </small>
                }
              />
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              Spend Tier (PAU status):
              <br />
              <small className="text-muted">Lifetime spend, refreshed daily</small>
            </Col>
            <Col md={8}>
              <Form.Check
                type="radio"
                id={`legacy-drop-spend-tier-all-${drop.id}`}
                checked={filterSpendTiers.length === 0}
                onChange={() => setFilterSpendTiers([])}
                label={
                  <>
                    All users <small className="text-muted">no spend filter</small>
                  </>
                }
              />
              {SPEND_TIER_OPTIONS.map(({ tier, label, range }) => (
                <Form.Check
                  key={tier}
                  type="checkbox"
                  id={`legacy-drop-spend-tier-${tier}-${drop.id}`}
                  checked={filterSpendTiers.includes(tier)}
                  onChange={() => toggleSpendTier(tier)}
                  label={
                    <>
                      {label} <small className="text-muted">{range}</small>
                      {spendTierAudienceByTier[tier] && (
                        <small className="ml-2">
                          {spendTierAudienceByTier[tier].totalUsers.toLocaleString()} users
                        </small>
                      )}
                    </>
                  }
                />
              ))}
              {allSpendTiersSelected && (
                <small className="text-muted">
                  Every tier selected - this saves as <strong>All users</strong> (no spend filter), which also reaches
                  payers whose tier has not been synced yet.
                </small>
              )}
              <div>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-2"
                  disabled={spendTierAudienceLoading}
                  onClick={() =>
                    loadSpendTierAudience({
                      variables: {
                        filters: {
                          userIds: textToUserIds(filterUserIds),
                          excludedUserIds: textToUserIds(filterExcludedUserIds),
                          userLevel: optionalNumber(filterUserLevel) ?? undefined,
                          userMinCreationDate: filterMinCreation,
                          userMaxCreationDate: filterMaxCreation,
                          noSubscriptionBefore: filterHadSub,
                        },
                      },
                    })
                  }
                >
                  {spendTierAudienceLoading ? 'Estimating…' : 'Estimate audience'}
                </Button>
              </div>
              {spendTierComputedAt && (
                <small className="text-muted">
                  Tiers computed {new Date(spendTierComputedAt).toLocaleString()} - lifetime spend, refreshed daily (can
                  be up to 24h behind). Counts every payer that matches this sale config&apos;s other matchers too,
                  whenever they last played. NPAU is every user without a tier row, so it is not counted here.
                  {audienceIgnoredMatchers.length > 0 && (
                    <strong>
                      {' '}
                      Not accounted for: {audienceIgnoredMatchers.join(' and ')} - the real audience is smaller.
                    </strong>
                  )}
                </small>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              Excluded user ids:
              <br />
              (separated by commas)
              <br />
              <small className="text-muted">Always wins over the matchers above</small>
            </Col>
            <Col md={8}>
              <Form.Control
                as="textarea"
                value={filterExcludedUserIds}
                onChange={(e) => setFilterExcludedUserIds(e.target.value)}
              />
            </Col>
          </Row>
        </Section>

        <Section title="Price and seats" hint="What the player pays, and how many seats are offered.">
          <Row className="mb-3">
            <Col md={3}>Price point:</Col>
            <Col md={6}>
              <Form.Control
                as="select"
                value={skuId}
                isInvalid={!selectedSku}
                onChange={(e) => setSkuId(e.target.value)}
              >
                {/* A price point no longer in the backend list: shown as is, instead of the browser
                  silently displaying the first option while the form keeps the unknown id. */}
                {!selectedSku && (
                  <option value={skuId} disabled>
                    {skuId} - unknown price point, pick one
                  </option>
                )}
                {skuOptions
                  .filter((option) => !option.retired || option.id === drop.skuId)
                  .map((option) => (
                    <option key={option.id} value={option.id}>
                      {skuOptionLabel(option)}
                    </option>
                  ))}
              </Form.Control>
              {!selectedSku && (
                <Form.Control.Feedback type="invalid">
                  Price point {skuId} is not in the backend list: pick one to see the prices.
                </Form.Control.Feedback>
              )}
              {selectedSku && (
                <Form.Text className="text-muted">
                  iOS <code>{selectedSku.iosSku}</code> · Android <code>{selectedSku.androidSku}</code>
                </Form.Text>
              )}
              {selectedSku && (selectedSku.iosPlaceholder || selectedSku.androidPlaceholder) && (
                <Form.Text className="text-warning">
                  The store product does not exist yet on{' '}
                  {[selectedSku.iosPlaceholder && 'iOS', selectedSku.androidPlaceholder && 'Android']
                    .filter(Boolean)
                    .join(' and ')}
                  : players there are not offered this sale config.
                </Form.Text>
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label className="font-weight-bold">Discount</Form.Label>
              <Form.Control as="select" value={discount} onChange={(e) => setOfferPercentage(e.target.value)}>
                {DISCOUNT_OPTIONS.map(({ name: label, value }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Control>
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold">Old price</Form.Label>
              <p>$ {oldPrice ?? '--'}</p>
            </Col>
            <Col md={3}>
              <Form.Label className="font-weight-bold">New Price</Form.Label>
              <p>$ {selectedSku?.cost ?? '--'}</p>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={3}>Seat cap:</Col>
            <Col md={2} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id={`legacy-drop-cap-enabled-${drop.id}`}
                label={capEnabled ? 'On' : 'Off - no limit'}
                checked={capEnabled}
                onChange={(e) => setCapEnabled(e.target.checked)}
              />
            </Col>
            <Col md={2}>
              <Form.Control
                type="number"
                min={0}
                value={capSeats}
                disabled={!capEnabled}
                onChange={(e) => setCapSeats(e.target.value)}
              />
            </Col>
            <Col md={4} className="d-flex align-items-center">
              Sold: {drop.seatsSold}
              {drop.capEnabled && drop.seatsSold > drop.capSeats && (
                <span className="text-muted ml-2">(oversell, accepted)</span>
              )}
            </Col>
          </Row>
        </Section>

        <Section title="Push notifications">
          <p className="text-muted">
            Sent at the drop start (START) or end (STOP) plus the offset in hours, e.g. STOP -24 = one day before the
            end. Title and text are optional: left empty, the default translated text is sent in each player&apos;s
            language; filled in, that exact text is sent to everyone.
          </p>
          <p className="text-muted">
            Checked every 15 minutes. A push goes out only while this sale config is on sale (feature on, ACTIVE, inside
            its dates, not sold out), to the players who would see it in the shop (offline, not owners, not kids,
            matching the audience, &quot;sales&quot; notifications on). A push more than 2 hours late is dropped: a time
            after the end never sends, and a time before the start sends at the start only when it is less than 2 hours
            before it.
          </p>
          <Table size="sm">
            <thead>
              <tr>
                <th>Anchor</th>
                <th>Offset (hours)</th>
                <th>Title / text (optional)</th>
                <th>Fires at</th>
                <th>Sent</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {schedule.map((entry, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  <td>
                    <Form.Control
                      as="select"
                      value={entry.anchor}
                      onChange={(e) =>
                        updateScheduleEntry(index, { anchor: e.target.value as SongpopLegacyPackNotifAnchor })
                      }
                    >
                      {Object.values(SongpopLegacyPackNotifAnchor).map((anchor) => (
                        <option key={anchor} value={anchor}>
                          {anchor}
                        </option>
                      ))}
                    </Form.Control>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      value={entry.offsetHours}
                      onChange={(e) => updateScheduleEntry(index, { offsetHours: Number(e.target.value) })}
                    />
                  </td>
                  <td style={{ minWidth: 280 }}>
                    <Form.Control
                      className="mb-1"
                      placeholder="default title"
                      maxLength={MAX_NOTIF_TITLE_LENGTH}
                      value={entry.title ?? ''}
                      onChange={(e) => updateScheduleEntry(index, { title: e.target.value })}
                    />
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="default text"
                      maxLength={MAX_NOTIF_BODY_LENGTH}
                      value={entry.body ?? ''}
                      onChange={(e) => updateScheduleEntry(index, { body: e.target.value })}
                    />
                  </td>
                  <td>{previewFireDate(entry)}</td>
                  <td>{drop.notifSchedule[index]?.sent ? 'yes' : '-'}</td>
                  <td>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => setSchedule((previous) => previous.filter((__, i) => i !== index))}
                    >
                      <i className="fas fa-trash" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() =>
              setSchedule((previous) => [...previous, { anchor: SongpopLegacyPackNotifAnchor.START, offsetHours: 0 }])
            }
          >
            <i className="fas fa-plus mr-2" />
            Add push
          </Button>
        </Section>

        {/* Floating save: always reachable without scrolling back to the top bar. */}
        <div
          className="d-flex justify-content-end align-items-center bg-white border-top py-2 px-3 shadow"
          style={{ position: 'sticky', bottom: 0, zIndex: 10 }}
        >
          {saveError && <span className="text-danger mr-3 text-truncate">{saveError.message}</span>}
          <Button
            variant="success"
            disabled={saving || !isDirty}
            title={isDirty ? '' : 'Nothing changed'}
            onClick={handleSave}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </div>
      </Container>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete sale config</Modal.Title>
        </Modal.Header>
        <Modal.Body>This cannot be undone. Delete the sale config &quot;{drop.name}&quot;?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setShowDeleteModal(false);
              deleteDrop({ variables: { id: drop.id } }).catch(() => {
                // surfaced through deleteError
              });
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
